import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'sonner';

import { RunStatus } from '../../../../backend/src/types/types';
import { APIBaseChronos } from '@/api/hosts';
import { QueryEntity } from '@/api/types';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';

interface Props {
  runId: string;
  status: RunStatus;
}

/** Global-admin only status override, used from the admin run management page. */
export const useUpdateRunStatus = () => {
  const { getFetchConfig } = useGetFetchConfig();
  const queryClient = useQueryClient();

  const updateRunStatus = async ({ runId, status }: Props) => {
    const fetchConfig = getFetchConfig({
      method: FetchMethod.PATCH,
      data: { status },
    });

    const response = await fetch(`${APIBaseChronos}/api/run/${runId}/status`, fetchConfig);
    if (!response.ok) {
      const errorData = (await response.json().catch(() => ({}))) as { message?: string };
      throw new Error(errorData.message ?? 'Failed to update run status');
    }
  };

  return useMutation({
    mutationFn: updateRunStatus,
    onSuccess: () => {
      toast.success('Run status updated');
      queryClient.invalidateQueries([QueryEntity.AllActiveRuns]);
      queryClient.invalidateQueries([QueryEntity.ActiveRuns]);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
};
